import { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import type { SeasonContext } from '../lib/seasonContext.ts';
import { points } from '../lib/format.ts';
import { fetchOverrides } from '../lib/api.ts';
import { NoGamesYet } from '../components/EmptyState.tsx';
import { ManagerLink } from '../components/ManagerLink.tsx';

/**
 * A draft-pick award — the race to the bottom (or top) among first-round picks.
 *
 * Sourced from the real draft board, not the old script's "whoever sat first in
 * the lineup", and ranked so the manager who pays is first. Which award this is
 * comes from the URL and its wording from the league's own config, so a league
 * that calls it something else gets its own page for free.
 */
export function DraftAward() {
  const { awardId } = useParams<{ awardId: string }>();
  const { stored, nameOf } = useOutletContext<SeasonContext>();
  const rule = stored.bundle.money.config.awards.find((a) => a.id === awardId);
  // Any game played or live, not a fully-final week: a live week still counts.
  const started = stored.bundle.data.results.length > 0;
  // But the crown depends on FIRST-ROUND scoring, which is not the same thing.
  // A Thursday night game between two later-round picks makes `started` true
  // while every first-rounder is still on zero — and a twelve-way tie at zero
  // would crown all twelve. Wait for somebody's pick to actually score.
  //
  // Nonzero, not positive: a first-rounder can post a negative total (a QB
  // throwing picks, a defense getting run over), and that manager is already
  // the live worst pick — exactly the person this page exists to name.
  const scored = stored.bundle.firstRound.some((e) => e.total !== 0);
  // Lowest total first (worst pick), then name so ties and no-games seasons
  // aren't in arbitrary order.
  // Only used to grey out and label who was ruled out — the crown itself comes
  // from the money board. A failed read here costs a badge, not a wrong answer.
  const [ineligible, setIneligible] = useState<string[]>([]);
  useEffect(() => {
    if (!awardId) return;
    fetchOverrides(stored.bundle.data.season)
      .then((o) => setIneligible(o.ineligible?.[awardId] ?? []))
      .catch(() => setIneligible([]));
  }, [stored.bundle.data.season, awardId]);

  // Before a snap, every total is 0 and "worst first" is meaningless — read it
  // as the draft board instead, in pick order.
  // Worst-first when the award pays the lowest total, best-first when it pays
  // the highest — whichever way, the manager the page is about leads it.
  const worstFirst = rule?.compute?.kind !== 'draftPickExtreme' || rule.compute.pick === 'low';
  const board = [...stored.bundle.firstRound].sort((a, b) =>
    scored
      ? (worstFirst ? a.total - b.total : b.total - a.total) ||
        nameOf(a.ownerId).localeCompare(nameOf(b.ownerId))
      : a.pick - b.pick,
  );
  // Who actually pays is decided by the money board, which already applies
  // eligibility and holds the award pending until there's scoring. Recomputing
  // it here would be a second implementation of a rule about money, and it
  // would disagree the moment the overrides read failed.
  const winnerIds = new Set(
    stored.bundle.money.awards.find((a) => a.id === awardId)?.winnerIds ?? [],
  );

  if (!rule) {
    return (
      <p className="font-mono text-xs text-chalk-dim">
        No such award this season.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="eyebrow">
          {stored.bundle.data.season} · {worstFirst ? 'lowest' : 'highest'} first-round pick
        </p>
        <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-tight">
          {rule?.label ?? 'Draft award'}
        </h1>
        <p className="mt-2 max-w-lg text-sm text-chalk-dim">
          You spent the first pick on him. Here's what it got you.
          {rule ? ` ${rule.description}` : ''}
        </p>
      </div>

      {/* Before kickoff there are no totals, but there ARE picks — and "who
          took whom in round one" is the thing people want to see the week after
          a draft. An empty state here wasted the most interesting page of
          September. */}
      {!scored && board.length > 0 && (
        <p className="font-mono text-xs text-chalk-dim">
          {started
            ? "No first-round points on the board yet. Here's what there is to lose with."
            : "No games played yet — everyone's at zero. Here's what there is to lose with."}
        </p>
      )}
      {board.length === 0 && <NoGamesYet season={stored.bundle.data.season} />}

      {board.length > 0 && (
      <div className="space-y-2">
        {board.map((e, i) => {
          const out = ineligible.includes(e.ownerId);
          const isWinner = winnerIds.has(e.ownerId);
          return (
            <div
              key={e.ownerId}
              className={`card flex items-center gap-4 p-4 ${
                isWinner ? 'ring-1 ring-blood/50' : ''
              } ${out ? 'opacity-50' : ''}`}
            >
              <div className="font-mono text-sm text-chalk-dim tabular">
                {String(scored ? i + 1 : e.pick).padStart(2, '0')}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-display font-bold">
                    <ManagerLink ownerId={e.ownerId}>{nameOf(e.ownerId)}</ManagerLink>
                  </span>
                  {isWinner && <span className="eyebrow text-blood">🤡</span>}
                  {out && <span className="eyebrow text-chalk-dim">not eligible</span>}
                </div>
                <div className="truncate text-xs text-chalk-dim">
                  Pick {e.pick} · {e.playerName} ({e.position})
                </div>
              </div>
              <div
                className={`font-mono text-2xl font-bold tabular ${
                  isWinner ? 'text-blood' : 'text-chalk'
                }`}
              >
                {points(e.total)}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
